import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { getProducts, getCuratorCollections, getProductRefinements, getActiveRefinementObjects } from '../../APIClient/shop';
import { getConsultsForUserIdOrUsername } from '../../APIClient/consults';
import {
  saveSearchResultsCache,
  updateCuratorGroup,
  updateCuratorGroupFavorite,
  setActiveCurator,
  setActiveCuratorGroup,
  setAttributableCuratorId,
  setAttributableCuratorGroupId,
  syncHierarchy
} from '../../Actions/ShopActions';
import { setAnalyticsMode } from '../../Actions/UIActions';

import { getUserId, getFavoriteCuratorGroupWithId, getCuratorGroupWithId, getSectionsForCollections } from '../../Helpers/user_helpers';
import { isAnalyticsMode } from '../../Helpers/ui_helpers';
import { getUrlParam } from '../../Helpers/helpers';
import { getShopAnalytics } from '../../APIClient/users';

const LOG_FOR_DEBUGGING = false;

const ShopData = props => {
  // Miscellaneous local updates that require re-fetching
  const localGroup = props.CuratorGroup_id ? getCuratorGroupWithId(props.user, props.CuratorGroup_id) : null;
  const localGroupNumUsers = localGroup?.num_curators;
  const forceReloadCuratorGroupHash = localGroup ? localGroupNumUsers : false;

  // Catch updates to main filtering
  const hierarchyHash = [`c${props.Category_id}`, `d${props.Department_id}`, `i${props.Industry_id}`, `b${props.AllBrand_id}`].join('-');
  const curatorHash = [`c${props.Curator_id}`, `c${props.Curator_username}`, `cg${props.CuratorGroup_id}`, `f${forceReloadCuratorGroupHash}`].join(
    '-'
  );
  const tabHash = [`t${props.tab}`].join('-');
  const pagingHash = [`l${props.limit}`, `p${props.page}`].join('-');
  const queryHash = [`q${props.query}`].join('-');
  const collectionHash = [
    `c${props.isShowingCollections ? 'yes' : 'no'}`,
    `s${props.Section_id}`,
    `t${props.Section_title}`,
    `a${props.showArchivedCollections ? 'yes' : 'no'}`,
    `s${props.sectionType}`
  ].join('-');
  const consultHash = [`c${props.isShowingConsults ? 'yes' : 'no'}`].join('-');
  const userSectionsHash = getSectionsForCollections(props.user)
    .map(section => `s${section.id}-${section.previewType}`)
    .join('-');

  const filterParams = {
    // Catalog Based
    Category_id: props.Category_id,
    Department_id: props.Department_id,
    Industry_id: props.Industry_id,
    AllBrand_id: props.AllBrand_id,

    // Curators Based
    Curator_username: props.Curator_username,
    Curator_id: props.Curator_id,
    CuratorGroup_id: props.CuratorGroup_id,

    // You Based
    User_id: getUserId(props.user)
  };

  const tabParams = {
    tab: props.tab
  };

  const queryParams = {
    query: props.query
  };

  /* Reset Results on Filter Change */
  React.useEffect(() => {
    props.setHasMoreResults(true);
  }, [hierarchyHash, curatorHash, queryHash, tabHash, collectionHash]);

  /* 
    Caching Strategy

    Store the results on the dismount so that we can quickly reload them if the hash is the same to ensure
    we keep our place even in the case of infinite scroll.

    !! Only store values here that would not show up in the URL.
  */
  const memoryRef = React.useRef({});
  const hasCachedResults = window.location.href === props.shop.searchResultsCache?.key;
  const [usingCachedResults, setUsingCachedResults] = React.useState(hasCachedResults);
  memoryRef.current = {
    key: window.location.href,
    page: props.page,
    hasMoreResults: props.hasMoreResults,
    productResults: props.productResults,
    productRefinements: props.productRefinements,
    activeProductRefinements: props.activeProductRefinements,
    activeRefinementObjects: props.activeRefinementObjects,
    collectionResults: props.collectionResults,
    sectionResults: props.sectionResults,
    consultResults: props.consultResults
  };

  React.useEffect(() => {
    if (hasCachedResults) {
      props.setIsFetchingResults(false);
      props.setIsFetchingProductRefinements(false);
      props.setIsFetchingActiveRefinementObjects(false);
      props.setPage(props.shop.searchResultsCache.data.page || 0);
      props.setHasMoreResults(props.shop.searchResultsCache.data.hasMoreResults || false);
      props.setProductResults(props.shop.searchResultsCache.data.productResults || []);
      props.setProductRefinements(props.shop.searchResultsCache.data.productRefinements || []);
      props.setActiveProductRefinements(props.shop.searchResultsCache.data.activeProductRefinements || []);
      props.setActiveRefinementObjects(props.shop.searchResultsCache.data.activeRefinementObjects || {});
      props.setCollectionResults(props.shop.searchResultsCache.data.collectionResults || []);
      props.setSectionResults(props.shop.searchResultsCache.data.sectionResults || []);
      props.setConsultResults(props.shop.searchResultsCache.data.consultResults || []);
      setTimeout(() => setUsingCachedResults(false), 250);
    }

    return () => {
      props.saveSearchResultsCache(memoryRef.current.key, memoryRef.current);
    };
  }, []);

  // Store current hashes in ref to ensure response is for the current request
  const searchHash = [hierarchyHash, curatorHash, pagingHash, tabHash, queryHash, collectionHash, consultHash].join('-');
  const searchHashRef = React.useRef(searchHash);
  React.useEffect(() => {
    searchHashRef.current = searchHash;
  }, [searchHash]);

  /* Fetch Core Collection Results */
  React.useEffect(() => {
    if (usingCachedResults && !props.isYourShop) return; // Allow refetching of collections for your shop in case of changes

    // Fetch Collections
    if (props.isShowingCollections) {
      !usingCachedResults && props.setIsFetchingResults(true); // Avoid showing loading state if we are using cached results
      const limit = usingCachedResults
        ? props.shop.searchResultsCache.data.collectionResults?.length || 24
        : props.isYourShop // If we are on your shop, we want to fetch all collections so you can rearrange them
        ? 1e6
        : props.limit || 24;
      const params = {
        showHidden: props.isYourShop,
        Curator_id: props.Curator_id,
        Curator_username: props.Curator_username,
        CuratorGroup_id: props.CuratorGroup_id,
        Section_id: props.Section_id,
        Section_title: props.Section_title,
        showArchivedCollections: props.showArchivedCollections,
        sectionType: props.sectionType,
        limit,
        page: props.page
      };
      getCuratorCollections(_.omitBy(params, val => !val))
        .then(resp => {
          if (searchHashRef.current !== searchHash) {
            return; // Ignore stale responses
          }
          props.setCollectionResults([...props.collectionResults.slice(0, props.page * limit), ...resp.collections]);
          props.setSectionResults(resp.sections || []);
          props.setHasMoreResults(resp.collections.length >= limit);
          props.setHadErrorResults(false);
          props.setIsFetchingResults(false);
        })
        .catch(err => {
          console.error(err);
          window.ALERT.error(typeof err === 'string' ? err : 'Unknown error');
          props.setHadErrorResults(true);
          props.setIsFetchingResults(false);
        });
    }
  }, [curatorHash, pagingHash, queryHash, collectionHash, userSectionsHash]);

  /* Fetch Consult Results */
  React.useEffect(() => {
    if (props.isShowingConsults && props.isCuratorShop) {
      props.setIsFetchingResults(true);
      getConsultsForUserIdOrUsername(props.Curator_username || props.Curator_id)
        .then(resp => {
          props.setConsultResults(resp.consults);
          props.setIsFetchingResults(false);
        })
        .catch(err => {
          console.error(err);
          props.setHadErrorResults(true);
          props.setIsFetchingResults(false);
        });
    }
  }, [consultHash]);

  /* Fetch Core Product Results */
  React.useEffect(() => {
    LOG_FOR_DEBUGGING && console.info(usingCachedResults ? `Using Cached Results for page ${props.page}` : `Fetching Results for page ${props.page}`);
    if (usingCachedResults) return;

    // Otherwise, Fetch Products
    if (!props.isShowingCollections && !props.isShowingConsults) {
      props.setIsFetchingResults(true);
      const limit = props.limit || 12;
      const params = {
        ...filterParams,
        ...tabParams,
        ...queryParams,
        showHidden: props.isYourShop,
        limit,
        page: props.page
      };
      getProducts(_.omitBy(params, val => !val))
        .then(resp => {
          if (searchHashRef.current !== searchHash) return; // Ignore stale responses
          props.setProductResults([...props.productResults.slice(0, props.page * limit), ...resp.results]);
          props.setHasMoreResults(resp.results.length === limit);
          props.setHadErrorResults(false);
          props.setIsFetchingResults(false);
        })
        .catch(err => {
          console.error(err);
          window.ALERT.error(typeof err === 'string' ? err : 'Unknown error');
          props.setHadErrorResults(true);
          props.setIsFetchingResults(false);
        });
    }
  }, [hierarchyHash, curatorHash, pagingHash, tabHash, queryHash]);

  // Store current hashes in ref to ensure response is for the current request
  const refinementSearchHash = [hierarchyHash, curatorHash].join('-');
  const refinementSearchHashRef = React.useRef(refinementSearchHash);
  React.useEffect(() => {
    refinementSearchHashRef.current = refinementSearchHash;
  }, [searchHash]);

  /* Fetch Filters */
  React.useEffect(() => {
    props.setIsFetchingProductRefinements(true);

    const params = _.omitBy(
      {
        ...filterParams,
        ...queryParams,
        skipRefinements: !!props.query || props.isShowingCollections || null
      },
      val => !val
    );

    // Get Product Refinements
    getProductRefinements(params)
      .then(resp => {
        if (refinementSearchHashRef.current !== refinementSearchHash) return; // Ignore stale responses
        props.setProductRefinements(resp.refinements);
        props.setActiveProductRefinements(resp.activeRefinements);
        props.setHadErrorRefinements(false);
      })
      .catch(err => {
        console.error(err);
        window.ALERT.error(typeof err === 'string' ? err : 'Unknown error');
        props.setHadErrorRefinements(true);
      })
      .then(() => {
        props.setIsFetchingProductRefinements(false);
      });
  }, [hierarchyHash, curatorHash, queryHash]);

  /* Fetch Active Refinement Objects */
  React.useEffect(() => {
    props.setIsFetchingActiveRefinementObjects(true);

    // Get Active Refinement Objects
    const params = _.omitBy({ ...filterParams, ...queryParams }, val => !val);
    getActiveRefinementObjects(params)
      .then(resp => {
        if (refinementSearchHashRef.current !== refinementSearchHash) return; // Ignore stale responses
        props.setActiveRefinementObjects(resp);
        const { activeCurator, activeCuratorGroup, activeCategory, activeDepartment, activeIndustry, activeBrands, activeSections } = resp || {};
        if (activeCurator) {
          props.setActiveCurator(activeCurator);
          props.setAttributableCuratorId(activeCurator.id);
        } else if (activeCuratorGroup) {
          props.setActiveCuratorGroup(activeCuratorGroup);
          props.setAttributableCuratorGroupId(activeCuratorGroup.id);
        } else {
          props.setActiveCurator(null);
          props.setActiveCuratorGroup(null);
          props.setAttributableCuratorId(null);
          props.setAttributableCuratorGroupId(null);
        }

        // Set the image style enum based on the hierarchy refinement objects
        if (activeCategory && activeCategory.shopProductResultsPageImageFormat)
          props.setImageStyleEnum(activeCategory.shopProductResultsPageImageFormat);
        if (activeDepartment && activeDepartment.shopProductResultsPageImageFormat)
          props.setImageStyleEnum(activeDepartment.shopProductResultsPageImageFormat);
        if (activeIndustry && activeIndustry.shopProductResultsPageImageFormat)
          props.setImageStyleEnum(activeIndustry.shopProductResultsPageImageFormat);
        if (activeBrands && activeBrands[0] && activeBrands[0].shopProductResultsPageImageFormat)
          props.setImageStyleEnum(activeBrands[0].shopProductResultsPageImageFormat);

        if (activeCurator) {
          // Confirm we can in fact load the current section based on the settings of the curator
          const goToFirstSection = () => {
            const isTryingToGoToCollections = props.tab === 'collections';
            const firstCollectionSection = activeSections.find(s => !s.smartSectionType);
            isTryingToGoToCollections
              ? props.goToSectionId(firstCollectionSection?.id)
              : activeSections[0]?.smartSectionType
              ? props.goToTab(activeSections[0]?.smartSectionType)
              : props.goToSectionId(activeSections[0]?.id);
          };

          // If we are on a tab that doesn't exist (or was hidden and not returned), then we need to go to the first section
          const isLatestTabActiveAndNoLatestTab = props.tab === 'latest' && !activeSections.some(s => s.smartSectionType === 'latest');
          const isPopularTabActiveAndNoPopularTab = props.tab === 'popular' && !activeSections.some(s => s.smartSectionType === 'popular');
          if (isLatestTabActiveAndNoLatestTab || isPopularTabActiveAndNoPopularTab) {
            goToFirstSection();
          }

          // If no tab is specified in the URL, navigate to the first available section
          const isCollectionsTabUnspecifiedCollection = getUrlParam('tab') === 'collections' && !props.Section_id && !props.Section_title;
          if (!getUrlParam('tab') || isCollectionsTabUnspecifiedCollection) {
            goToFirstSection();
          }
        }
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        props.setIsFetchingActiveRefinementObjects(false);
      });
  }, [hierarchyHash, curatorHash]);

  /* Fetch Analytics */
  const analyticsOn = isAnalyticsMode(props.ui);
  const analyticsUserId = props.isYourShop && getUserId(props.user);

  React.useEffect(() => {
    if (analyticsOn && !props.currentAnalytics && analyticsUserId) {
      props.setIsFetchingAnalytics(true);
      getShopAnalytics(analyticsUserId)
        .then(({ analytics }) => {
          props.setIsFetchingAnalytics(false);
          props.setAnalyticsPerUserId({ [analyticsUserId]: analytics });
        })
        .catch(() => {
          window.ALERT.error('There was an issue fetching analytics for this shop.');
          props.setIsFetchingAnalytics(false);
          props.setAnalyticsMode(false);
        });
    }
  }, [analyticsOn, analyticsUserId]);

  /* Manage Changes in URLS */
  const stateVariablesInURL = [
    'Category_id',
    'Department_id',
    'Industry_id',
    'AllBrand_id',
    'Curator_id',
    'CuratorGroup_id',
    'Section_id',
    'Section_title',
    'sectionType',
    'showArchivedCollections',
    'tab',
    'query'
  ];
  // const prettyUrlNames = { Category_id: 'category', Department_id: 'department', Industry_id: 'industry', AllBrand_id: 'brand', Curator_id: 'curator', CuratorGroup_id: 'curatog_group', tab: 'tab'};

  // Check current URL state
  const curUrlSearchQuery = window.location.search;
  const activeParamsInCurrentUrl = {};
  const urlParams = new URLSearchParams(curUrlSearchQuery);
  urlParams.forEach((value, key) => (activeParamsInCurrentUrl[key] = value));

  /* State Change => URL Change */
  React.useEffect(() => {
    const newParams = new URLSearchParams();

    // Add state variables to the URL if they exist in props AND are not default
    stateVariablesInURL.forEach(param => {
      const isDefault = param === 'tab' && props[param] === props.defaultValuesIfNull.tab;
      if (props[param] && !isDefault) newParams.set(param, props[param]);
    });

    // Add all non state variables back to the URL if we use them for other things on this page
    const unrelatedUrlVariables = ['i', 'd'];
    unrelatedUrlVariables.forEach(param => {
      const paramValue = urlParams.get(param);
      if (paramValue) newParams.set(param, paramValue);
    });

    // Check the current state, ruling out any default values
    const currentParams = new URLSearchParams();
    stateVariablesInURL.forEach(param => {
      const isDefault = param === 'tab' && getUrlParam(param) === props.defaultValuesIfNull.tab;
      if (getUrlParam(param) && !isDefault) currentParams.set(param, getUrlParam(param));
    });

    // Update URL without reload if it differs from existing, regardless of order
    const hasParams = newParams.toString().length > 0;
    const newUrl = `${window.location.pathname}${hasParams ? '?' : ''}${newParams}`;
    if (currentParams.toString() !== newParams.toString()) {
      window.history.pushState({}, '', newUrl);
      LOG_FOR_DEBUGGING &&
        console.info('State => URL Update', {
          newUrl,
          stateVariablesInURL,
          newParams: newParams.toString(),
          currentParams: currentParams.toString()
        });
    }
  }, [hierarchyHash, curatorHash, tabHash, collectionHash]);

  /* URL Change => State Change */
  const [currentUrl, setCurrentUrl] = React.useState(window.location.href);
  React.useEffect(() => {
    const updateURL = () => setCurrentUrl(window.location.href);

    // Intercept history changes and popstate events
    const interceptHistory = method => {
      const original = window.history[method];
      window.history[method] = (...args) => {
        original.apply(window.history, args);
        updateURL();
      };
    };

    interceptHistory('pushState');
    interceptHistory('replaceState');
    window.addEventListener('popstate', updateURL);

    return () => {
      window.removeEventListener('popstate', updateURL);
    };
  }, [currentUrl]);

  // Update state based on URL
  const [completedFirstUrlToStateUpdate, setCompletedFirstUrlToStateUpdate] = React.useState(false);
  React.useEffect(() => {
    const stateUpdatesRequired = {};

    // If we have changed the "match" of the username we need to consider that a state variable
    const Curator_username_from_url = props.match.params?.username;

    // If we have a username in the state but not in the URL, we need to update the URL
    if (props.Curator_username && !Curator_username_from_url) {
      stateUpdatesRequired.Curator_username = null;
    }

    // Identify parameters that differ between state and URL
    stateVariablesInURL.forEach(param => {
      const urlValue = activeParamsInCurrentUrl[param];
      const stateValue = props[param];
      const isStringMatch = stateValue === urlValue;
      const isIntMatch = parseInt(stateValue) === parseInt(urlValue);
      const isNilMatch = !stateValue && !urlValue;

      if (!isStringMatch && !isIntMatch && !isNilMatch) {
        // Check if the param is an integer or string
        const isInteger = ['Category_id', 'Department_id', 'Industry_id', 'AllBrand_id', 'Curator_id', 'CuratorGroup_id', 'Section_id'].includes(
          param
        );
        const isString = ['tab', 'query', 'sectionType', 'Section_title', 'showArchivedCollections'].includes(param);
        if (!isInteger && !isString) console.error(`Unknown type for ${param}`);

        // Even if we strip these values we don't want to refresh
        const disallowNullValues = ['tab'];
        if (disallowNullValues.includes(param) && !urlValue) return;

        // Update state if the URL value is different
        stateUpdatesRequired[param] = !urlValue ? null : isInteger ? parseInt(activeParamsInCurrentUrl[param]) : activeParamsInCurrentUrl[param];
      }
    });

    // Apply necessary updates to state
    if (!_.isEmpty(stateUpdatesRequired)) {
      // Update state
      Object.entries(stateUpdatesRequired).forEach(([param, value]) => {
        const updateFunction = {
          Category_id: props.setCategory_id,
          Department_id: props.setDepartment_id,
          Industry_id: props.setIndustry_id,
          AllBrand_id: props.setAllBrand_id,
          Curator_id: props.setCurator_id,
          Curator_username: props.setCurator_username,
          CuratorGroup_id: props.setCuratorGroup_id,
          Section_id: props.setSection_id,
          Section_title: props.setSection_title,
          sectionType: props.setSectionType,
          showArchivedCollections: props.setShowArchivedCollections,
          tab: props.setTab,
          query: props.setQuery
        }[param];

        const wasDefault = props.defaultValuesIfNull[param] === props[param];
        const isSettingToNull = value === null;

        if (wasDefault && isSettingToNull && !completedFirstUrlToStateUpdate) return;
        else if (updateFunction) updateFunction(value);
        else console.error(`No update function found for ${param}`);
      });

      // We only want to use Curator_id or Curator_username, so reset if we see it
      if (stateUpdatesRequired.CuratorGroup_id) props.setCurator_username(null);
      if (stateUpdatesRequired.Curator_id) props.setCurator_username(null);
      if (stateUpdatesRequired.Curator_username) props.setCurator_id(null);

      LOG_FOR_DEBUGGING && console.info('URL => State Update', stateUpdatesRequired);

      // Reset paging
      props.setPage(0);
    }
    setCompletedFirstUrlToStateUpdate(true);
  }, [currentUrl]);

  // Handle updating the latest timestamp for the curator group when it was last seen
  const returnedGroupId = props.activeRefinementObjects?.activeCuratorGroup?.id; // Make sure it was already returned so we know the group has the initial userLastViewedAt
  React.useEffect(() => {
    const canLogSeenRecentProducts = props.tab === 'latest' && !props.query;

    // Update the latest timestamp for the curator group when it was last seen
    if (canLogSeenRecentProducts && props.viewingMyGroup) {
      const group = getCuratorGroupWithId(props.user, returnedGroupId);
      group && props.updateCuratorGroup(group, { userLastViewedAt: new Date() });
    }

    // Update the latest timestamp for the curator group favorite when it was last seen
    if (canLogSeenRecentProducts && props.viewingFavoritedGroup) {
      const groupFavorite = getFavoriteCuratorGroupWithId(props.user, returnedGroupId);
      groupFavorite && props.updateCuratorGroupFavorite(groupFavorite, { userLastViewedAt: new Date() });
    }
  }, [returnedGroupId, props.tab]);

  // Sync Hierarchy
  React.useEffect(() => {
    props.syncHierarchy();
  }, []);

  // Scrolling Management
  // If changing curators, scroll to the top of the page
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [curatorHash]);

  // If changing search query, scroll to the top of the page
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [queryHash]);

  // If changing tabs on hierarchy pages, scroll to the top of the page
  React.useEffect(() => {
    if (!props.shopControlsRef.current) return;
    const elTopToContainer = props.shopControlsRef.current.getBoundingClientRect().top - 44; // 44px is the height of the header
    if (elTopToContainer < 0) {
      window.scrollTo({ top: window.scrollY + elTopToContainer, behavior: 'smooth' });
    }
  }, [hierarchyHash, collectionHash]);

  return null;
};

ShopData.propTypes = {
  user: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,

  // Hierarchy Based
  Category_id: PropTypes.number,
  Department_id: PropTypes.number,
  Industry_id: PropTypes.number,
  AllBrand_id: PropTypes.number,

  // Curators Based
  Curator_id: PropTypes.number,
  CuratorGroup_id: PropTypes.number,

  // Tabs
  tab: PropTypes.string,
  setTab: PropTypes.func,

  // Search Query
  query: PropTypes.string,
  setQuery: PropTypes.func.isRequired,

  // Collections
  isShowingCollections: PropTypes.bool.isRequired,
  Section_id: PropTypes.number,
  Section_title: PropTypes.string,
  setSection_id: PropTypes.func.isRequired,
  setSection_title: PropTypes.func.isRequired,

  // Paging
  page: PropTypes.number.isRequired,
  hasMoreResults: PropTypes.bool.isRequired,
  setPage: PropTypes.func.isRequired,
  setHasMoreResults: PropTypes.func.isRequired,

  // Errors
  setHadErrorResults: PropTypes.func.isRequired,
  setHadErrorRefinements: PropTypes.func.isRequired,

  // Results & Refinements
  setProductResults: PropTypes.func.isRequired,
  setProductRefinements: PropTypes.func.isRequired,
  setActiveProductRefinements: PropTypes.func.isRequired,
  setActiveRefinementObjects: PropTypes.func.isRequired,
  saveSearchResultsCache: PropTypes.func.isRequired,

  // Fetching
  setIsFetchingResults: PropTypes.func.isRequired,
  isFetchingResults: PropTypes.bool.isRequired,
  setIsFetchingProductRefinements: PropTypes.func.isRequired,
  isFetchingProductRefinements: PropTypes.bool.isRequired,
  setIsFetchingActiveRefinementObjects: PropTypes.func.isRequired,
  isFetchingActiveRefinementObjects: PropTypes.bool.isRequired,

  // Analytics
  setIsFetchingAnalytics: PropTypes.func.isRequired,
  isFetchingAnalytics: PropTypes.bool.isRequired,
  setAnalyticsPerUserId: PropTypes.func.isRequired,
  analyticsPerUserId: PropTypes.object.isRequired,
  setAnalyticsMode: PropTypes.func.isRequired,

  // Handle updating latest
  updateCuratorGroup: PropTypes.func.isRequired,
  updateCuratorGroupFavorite: PropTypes.func.isRequired,
  viewingMyGroup: PropTypes.bool.isRequired,
  viewingFavoritedGroup: PropTypes.bool.isRequired,

  // UI
  setImageStyleEnum: PropTypes.func.isRequired,
  imageStyleEnum: PropTypes.string.isRequired,

  // Defaults to ensure valid url transitions
  defaultValuesIfNull: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { ui, user, shop } = state;
  return { ui, user, shop };
};

export default connect(mapStateToProps, {
  saveSearchResultsCache,
  setActiveCurator,
  setActiveCuratorGroup,
  setAttributableCuratorId,
  setAttributableCuratorGroupId,
  updateCuratorGroup,
  updateCuratorGroupFavorite,
  setAnalyticsMode,
  syncHierarchy
})(ShopData);
